import React, { useState, useEffect } from 'react';
import { LuX, LuUserPlus, LuCircleCheck, LuPhoneCall, LuMessageSquare, LuCircleHelp, LuTarget } from 'react-icons/lu';
import axios from 'axios';
import ModalPortal from '../../components/ui/ModalPortal';

const AddManualContactModal = ({ isOpen, onClose, initialTab = 'forms', onSuccess }) => {
  const [activeType, setActiveType] = useState(initialTab); // 'forms' | 'queries' | 'webinar' | 'leads'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Sync activeType to initialTab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveType(initialTab);
      setError('');
      setSuccessMsg('');
    }
  }, [isOpen, initialTab]);

  // Form State for all 4 types
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    comment: '',
    category: 'General',
    subject: '',
    description: '',
    status: 'open',
    countryCode: '+91',
    mobile: '',
    usTaxStatus: 'US Citizen / GC',
    currentlyLiving: 'India',
    filedBefore: 'No',
    questions: '',
    whatsapp: '',
    source: 'Phone Call / Manual Entry'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    const token = localStorage.getItem('adminToken');
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
    const headers = { Authorization: `Bearer ${token}` };

    try {
      let endpoint = '';
      let payload = {};

      if (activeType === 'forms') {
        if (!formData.name || !formData.email) {
          throw new Error('Name and Email are required');
        }
        endpoint = `${baseUrl}/api/contact/admin/manual`;
        payload = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          comment: formData.comment || 'Manually added by Admin'
        };
      } else if (activeType === 'queries') {
        if (!formData.email || !formData.subject || !formData.description) {
          throw new Error('Email, Subject, and Description are required');
        }
        endpoint = `${baseUrl}/api/contact/admin/queries/manual`;
        payload = {
          email: formData.email,
          category: formData.category,
          subject: formData.subject,
          description: formData.description,
          status: formData.status
        };
      } else if (activeType === 'webinar') {
        if (!formData.name || !formData.email) {
          throw new Error('Name and Email are required');
        }
        endpoint = `${baseUrl}/api/webinar/admin/manual`;
        payload = {
          name: formData.name,
          email: formData.email,
          countryCode: formData.countryCode,
          mobile: formData.mobile || formData.phone,
          usTaxStatus: formData.usTaxStatus,
          currentlyLiving: formData.currentlyLiving,
          filedBefore: formData.filedBefore,
          questions: formData.questions || 'Manually added by Admin'
        };
      } else if (activeType === 'leads') {
        if (!formData.name || !formData.email) {
          throw new Error('Name and Email are required');
        }
        endpoint = `${baseUrl}/api/leads/admin/manual`;
        payload = {
          name: formData.name,
          email: formData.email,
          whatsapp: formData.whatsapp || formData.phone,
          source: formData.source || 'Phone Call / Manual Entry'
        };
      }

      const response = await axios.post(endpoint, payload, { headers });

      if (response.data?.success) {
        setSuccessMsg('Entry manually added successfully!');
        setTimeout(() => {
          onSuccess?.(activeType);
          onClose();
        }, 800);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to add manual entry');
    } finally {
      setLoading(false);
    }
  };

  const typeTabs = [
    { id: 'forms', label: 'Contact Form', icon: LuPhoneCall },
    { id: 'queries', label: 'Query', icon: LuCircleHelp },
    { id: 'webinar', label: 'Webinar Entry', icon: LuMessageSquare },
    { id: 'leads', label: 'Landing Lead', icon: LuTarget }
  ];

  return (
    <ModalPortal open={isOpen} onClose={onClose} contentClassName="max-w-3xl w-full p-6 sm:p-8 relative">
      {/* Close Button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors cursor-pointer z-10"
      >
        <LuX className="w-5 h-5" />
      </button>

      {/* Modal Header */}
      <div className="flex items-center gap-3.5 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold shadow-xs border border-indigo-100 dark:border-indigo-900/50">
          <LuUserPlus className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Add Manual Lead / Entry
          </h3>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">
            Log external call inquiries or offline contacts into Contacts Hub
          </p>
        </div>
      </div>

      {/* Modern Segmented Tab Bar - Clean spacious grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6 p-1.5 bg-gray-100/80 dark:bg-dark-700/80 rounded-2xl border border-gray-200/50 dark:border-dark-600/50">
        {typeTabs.map((tab) => {
          const IconComp = tab.icon;
          const isActive = activeType === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveType(tab.id)}
              className={`flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 shadow-sm shadow-indigo-500/10 border border-gray-200/60 dark:border-dark-600 font-extrabold'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-dark-600/40'
              }`}
            >
              <IconComp className={`w-4 h-4 shrink-0 transition-transform ${isActive ? 'scale-110 text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-5 p-3.5 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 text-xs font-semibold">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="mb-5 p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-300 text-xs font-semibold flex items-center gap-2">
          <LuCircleCheck className="w-4 h-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Contact Forms Fields */}
        {activeType === 'forms' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. John Doe"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="client@example.com"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                Phone Number
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 9876543210"
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                Call Notes / Message *
              </label>
              <textarea
                name="comment"
                rows={3}
                value={formData.comment}
                onChange={handleChange}
                placeholder="Details about client call inquiry..."
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all resize-none"
              />
            </div>
          </>
        )}

        {/* Query Fields */}
        {activeType === 'queries' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="client@example.com"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all cursor-pointer"
                >
                  <option value="General">General</option>
                  <option value="US Expat Tax">US Expat Tax</option>
                  <option value="USA Incorporation">USA Incorporation</option>
                  <option value="Bookkeeping">Bookkeeping</option>
                  <option value="FEMA / ODI">FEMA / ODI</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all cursor-pointer"
                >
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="e.g. Phone query regarding US tax filing"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                Description *
              </label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                placeholder="Query details..."
                required
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all resize-none"
              />
            </div>
          </>
        )}

        {/* Webinar Fields */}
        {activeType === 'webinar' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. John Doe"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="client@example.com"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Phone / WhatsApp Number
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  US Tax Status
                </label>
                <select
                  name="usTaxStatus"
                  value={formData.usTaxStatus}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all cursor-pointer"
                >
                  <option value="US Citizen / GC">US Citizen / Green Card</option>
                  <option value="NRI / Non-resident">NRI / Non-resident</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                Currently Living In
              </label>
              <input
                type="text"
                name="currentlyLiving"
                value={formData.currentlyLiving}
                onChange={handleChange}
                placeholder="e.g. India"
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                Questions / Call Note
              </label>
              <textarea
                name="questions"
                rows={2}
                value={formData.questions}
                onChange={handleChange}
                placeholder="Questions or call notes..."
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all resize-none"
              />
            </div>
          </>
        )}

        {/* Landing Leads Fields */}
        {activeType === 'leads' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. John Doe"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="client@example.com"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Phone / WhatsApp Number
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1.5">
                  Lead Source / Tag
                </label>
                <input
                  type="text"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  placeholder="e.g. Phone Call / Offline Inquiry"
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs font-semibold text-gray-900 dark:text-white focus:bg-white dark:focus:bg-dark-800 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all"
                />
              </div>
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-5 border-t border-gray-100 dark:border-dark-700 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 rounded-xl text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2.5 rounded-xl text-xs font-extrabold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 transition-all shadow-md shadow-indigo-500/20 flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <LuUserPlus className="w-4 h-4" />
                <span>Save Manual Entry</span>
              </>
            )}
          </button>
        </div>
      </form>
    </ModalPortal>
  );
};

export default AddManualContactModal;
